import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import CrossIcon from "assets/icons/cross.svg?react";
import DropdownIcon from "assets/icons/dropdown-icon.svg?react";

import { CALCULATOR_TYPES } from "constants/calculator";

import {
  StagesProps,
  StagesFormProps,
  StagesFieldsProps,
} from "./Stages.types";

import { Typography } from "shared/ui/components/Typography";
import { Collapsible } from "shared/ui/components/Collapsible";

import { FormikField } from "components/FormikField";
import { InputField, MonthPickerInputField } from "components/FormikFields";

import { StagesActions } from "../StagesActions";

import styles from "./Stages.module.scss";

const Fields = ({
  t,
  calculatorType,
  stages,
  onRemoveStageClick,
}: StagesFieldsProps) => {
  return (
    <div className={styles.fields}>
      <div className={styles.fieldsColumn}>
        {stages.slice(4, 8).map((_: any, index: number) => (
          <div className={styles.fieldGroup}>
            <Typography variant="bodyM" className={styles.fieldGroupLabel}>
              {t("calculator_form.stages.stage_building")}
            </Typography>
            <FormikField
              name={`stages[${index + 4}].date`}
              component={MonthPickerInputField}
              controlProps={{
                label: "",
                variant: "horizontal",
                className: styles.fieldGroupDate,
              }}
              componentProps={{
                fieldDisabled: true,
                disabled: calculatorType === CALCULATOR_TYPES.LIMITED_PRO,
                component: "div"
              }}
            />
            <FormikField
              name={`stages[${index + 4}].fee`}
              component={InputField}
              controlProps={{
                label: "",
                variant: "horizontal",
                className: styles.fieldGroupPercentage,
              }}
              componentProps={{
                placeholder: "",
                type: "localeDe",
                // disabled: stagesLength === index + 3,
                disabled: calculatorType === CALCULATOR_TYPES.LIMITED_PRO,
                endAdornment: <Typography variant="bodyS">%</Typography>,
              }}
            />
            {(calculatorType === CALCULATOR_TYPES.STANDARD || calculatorType === CALCULATOR_TYPES.EXPERT) && (
              <CrossIcon
                onClick={() => onRemoveStageClick(index + 4)}
                className={styles.fieldGroupControlIcon}
              />
            )}
          </div>
        ))}
      </div>
      <div className={styles.fieldsColumn}>
        {stages.slice(8, 11).map((_: any, index: number) => (
          <div className={styles.fieldGroup}>
            <Typography variant="bodyM" className={styles.fieldGroupLabel}>
              {t("calculator_form.stages.stage_building")}
            </Typography>
            <FormikField
              name={`stages[${index + 8}].date`}
              component={MonthPickerInputField}
              controlProps={{
                label: "",
                variant: "horizontal",
                className: styles.fieldGroupDate,
              }}
              componentProps={{
                fieldDisabled: true,
                disabled: calculatorType === CALCULATOR_TYPES.LIMITED_PRO,
                component: "div"
              }}
            />
            <FormikField
              name={`stages[${index + 8}].fee`}
              component={InputField}
              controlProps={{
                label: "",
                variant: "horizontal",
                className: styles.fieldGroupPercentage,
              }}
              componentProps={{
                placeholder: "",
                type: "localeDe",
                // disabled: stagesLength === index + 7,
                disabled: calculatorType === CALCULATOR_TYPES.LIMITED_PRO,
                endAdornment: <Typography variant="bodyS">%</Typography>,
              }}
            />
            {(calculatorType === CALCULATOR_TYPES.STANDARD || calculatorType === CALCULATOR_TYPES.EXPERT) && (
              <CrossIcon
                onClick={() => onRemoveStageClick(index + 8)}
                className={styles.fieldGroupControlIcon}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const StagesDesktop = ({
  t,
  values,
  stagesOptions,
  calculatorType,
  onAddStageClick,
  onSortByDateClick,
  onRemoveStageClick,
}: StagesFormProps) => {
  const { errors } = useFormikContext<any>();

  return (
    <div className={styles.rootDesktop}>
      <StagesActions
        values={values}
        calculatorType={calculatorType}
        onAddStageClick={onAddStageClick}
        onSortByDateClick={onSortByDateClick}
      />

      <div className={styles.fieldsContainer}>
        <Fields
          t={t}
          calculatorType={calculatorType}
          stages={values.stages}
          stagesOptions={stagesOptions}
          onRemoveStageClick={onRemoveStageClick}
        />
      </div>

      <div className={styles.errorsDesktop}>
        {errors.stages && typeof errors.stages === "string" && (
          <Typography variant="bodyS" className={styles.errorsText}>
            {errors.stages}
          </Typography>
        )}
      </div>
    </div>
  );
};

const StagesMobile = ({
  t,
  values,
  stagesOptions,
  calculatorType,
  onAddStageClick,
  onSortByDateClick,
  onRemoveStageClick,
}: StagesFormProps) => {
  const { errors } = useFormikContext<any>();

  return (
    <Collapsible
      transitionTime={150}
      triggerTagName="div"
      className={styles.rootMobile}
      openedClassName={cn(styles.rootMobile, styles.rootMobileOpened)}
      trigger={
        <div className={cn(styles.header)}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("calculator_form.stages.title")}
          </Typography>
          <DropdownIcon className={styles.headerIcon} name="chevronDown" />
        </div>
      }
    >
      <Fields
        t={t}
        calculatorType={calculatorType}
        stages={values.stages}
        stagesOptions={stagesOptions}
        onRemoveStageClick={onRemoveStageClick}
      />

      <div className={styles.errorsMobile}>
        {errors.stages && typeof errors.stages === "string" && (
          <Typography variant="bodyS" className={styles.errorsText}>
            {errors.stages}
          </Typography>
        )}
      </div>

      <StagesActions
        values={values}
        calculatorType={calculatorType}
        onAddStageClick={onAddStageClick}
        onSortByDateClick={onSortByDateClick}
      />
    </Collapsible>
  );
};

const Stages = ({
  activeTab,
  isDesktop,
  values,
  stagesOptions,
  calculatorType,
  onAddStageClick,
  onSortByDateClick,
  onRemoveStageClick,
}: StagesProps) => {
  const { t } = useTranslation();

  if (!isDesktop) {
    return (
      <StagesMobile
        t={t}
        values={values}
        stagesOptions={stagesOptions}
        calculatorType={calculatorType}
        onAddStageClick={onAddStageClick}
        onSortByDateClick={onSortByDateClick}
        onRemoveStageClick={onRemoveStageClick}
      />
    );
  }

  if (activeTab !== "stages") {
    return null;
  }

  return (
    <StagesDesktop
      t={t}
      values={values}
      stagesOptions={stagesOptions}
      calculatorType={calculatorType}
      onAddStageClick={onAddStageClick}
      onSortByDateClick={onSortByDateClick}
      onRemoveStageClick={onRemoveStageClick}
    />
  );
};

export default Stages;
